import { AnnotationWorkspace } from "@/features/annotation/ui/annotation-workspace";
import { Timeline, TimelineRef } from "@/features/annotation/ui/timeline";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { Box, Flex } from "@radix-ui/themes";
import { convertHeightToPercent, yieldToMain } from "@/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector, useAppStore } from "@/app/appStore";
import dayjs from "dayjs";
import {
  resetState,
  selectSelectedOrder,
  setAnnotationSaving,
  setDateRange,
  setOrderLoading,
  setSelectedOrder,
} from "@/entities/annotation";
import {
  TimelineControls,
  NAVIGATION_MODES,
} from "@/features/annotation/ui/timeline-controls";
import { OrderWithJourneys } from "@/features/annotation/lib/helpers";
import ChromeUsageReminder from "@/shared/ui/chrome-usage-reminder";
import { isChrome } from "react-device-detect";
import AnnotationHeader from "@/features/annotation/ui/annotation-header";
import { usePlayerActions } from "@/shared/hooks/usePlayerActions";
import { useTimelineData } from "@/features/annotation/hooks/useTimelineData";
import { toast } from "react-hot-toast";
import Header from "@/shared/components/header";
import { LeftSidebar } from "./ui/left-sidebar";
import {
  useLazyOrderByIdQuery,
  useLazyGetLatestOrderInspectionQuery,
} from "@/shared/api/lod-api";
import { useUrlQueries } from "@/shared/hooks/useUrlQueries";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGondolaChangeGroupsData } from "./hooks/useGondolaChangeGroupsData";
import RightSidebar from "./ui/right-sidebar";
import {
  selectVisualizeTrackProjections,
  setVisualizeTrackProjections,
} from "@/entities/gondola-change-groups";
import { Checkbox } from "@/components/checkbox";
import { NavigationItems } from "@/shared/types/navigation";
import { ZoomPanInstructions } from "../annotation/ui/zoom-pan-instructions";
import { HandProjections } from "./components/hand-projections";
import TrackPins from "../sku-sections/components/track-pins";
import SkuSectionChangesPlanogram from "./ui/sku-section-changes-planogram";
import WristPoint from "./ui/wrist-point";
import LegendBox from "./ui/legend-box";
import { FrameAvailabilityStatus } from "./components/frame-availability-status";
import TrackPinsStatus from "@/shared/components/track-pins-status";

export function GondolaChangeGroupsView() {
  const { addTimelineOrderEvent, createOrderTimelineGroup, clearTimelineData } =
    useTimelineData();

  const { startVideo, updateVideoTimeRange } = usePlayerActions();
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const dispatch = useAppDispatch();
  const [getOrderById] = useLazyOrderByIdQuery();
  const [
    loadLatestOrderInspection,
    { isLoading: isLoadingLatestOrderInspection },
  ] = useLazyGetLatestOrderInspectionQuery();
  const { getAllQueries } = useUrlQueries();
  const navigate = useNavigate();
  const { storeKey = "", orderId: orderIdParam } = useParams<{
    storeKey?: string;
    orderId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const requestIdParam = searchParams.get("request_id");

  const [workspaceKey] = useState<string>(() => crypto.randomUUID());
  const store = useAppStore();

  const { orderId: jsonOrderId, loadFromEndpoint } =
    useGondolaChangeGroupsData();

  const visualizeTrackProjections = useAppSelector(
    selectVisualizeTrackProjections
  );

  const [isPlanogramViewVisible, setIsPlanogramViewVisible] = useState(true);

  useEffect(() => {
    return () => {
      clearTimelineData();
      dispatch(resetState());
    };
  }, [dispatch]);

  const populateAnnotationDetails = async (order: OrderWithJourneys) => {
    try {
      dispatch(setOrderLoading(true));

      await yieldToMain();

      addTimelineOrderEvent(order.id, new Date(order.time));
      createOrderTimelineGroup(order.id);
    } catch (error) {
      toast.error("Failed to populate annotation details");
      console.log("error ", error);
    } finally {
      dispatch(setAnnotationSaving(false));
      dispatch(setOrderLoading(false));
    }
  };

  const onSelectOrder = (order: OrderWithJourneys) => {
    const {
      annotation: { currentCamera, timelineRange },
    } = store.getState();
    const start = dayjs(order.time)
      .subtract(Number(timelineRange.beforeMinutes), "minute")
      .toDate();
    const end = dayjs(order.time)
      .add(Number(timelineRange.afterMinutes), "minute")
      .toDate();
    const orderTimestamp = new Date(order.time).getTime();

    const isLive =
      dayjs().diff(dayjs(order.time), "minute") <=
      Number(timelineRange.afterMinutes);

    dispatch(setSelectedOrder(order));
    dispatch(setDateRange({ start, end }));

    timelineRef.current?.setTimelineRange(start, end, {
      clearDataset: true,
      forceZoom: true,
    });

    startVideo({
      seekValueUTC: orderTimestamp,
      cameraId: currentCamera,
      isLive,
    });

    populateAnnotationDetails(order);
  };

  const timelineRef = useRef<TimelineRef | null>(null);

  const handleUpdateZoom = (zoomLevel: number) => {
    timelineRef.current?.adjustZoomLevel(zoomLevel);
  };

  const handleRangeChange = (params: {
    start: Date;
    end: Date;
    beforeMinutes: number;
    afterMinutes: number;
  }) => {
    const { start, end, afterMinutes } = params;
    const {
      annotation: { selectedOrder },
    } = store.getState();

    const isLive =
      dayjs().diff(dayjs(selectedOrder?.time), "minute") <= afterMinutes;

    timelineRef.current?.setTimelineRange(start, end, {
      clearDataset: false,
    });
    dispatch(setDateRange({ start, end }));
    updateVideoTimeRange({
      start,
      end,
      isLive,
    });
  };

  const loadSingleOrderData = useCallback(
    async (orderId: string) => {
      const { data: response } = await getOrderById(orderId);

      const order = response?.result;

      if (order) {
        onSelectOrder(order);
      } else {
        const currentQueries = getAllQueries();
        const queryString = new URLSearchParams(currentQueries).toString();

        navigate({
          pathname: `/stores/${storeKey}/gondola-change-groups`,
          search: queryString ? `?${queryString}` : "",
        });
      }
    },
    [getOrderById]
  );

  useEffect(() => {
    if (jsonOrderId && (!selectedOrder || selectedOrder.id !== jsonOrderId)) {
      loadSingleOrderData(jsonOrderId);
    }
  }, [jsonOrderId, selectedOrder, loadSingleOrderData]);

  useEffect(() => {
    if (orderIdParam) {
      const loadData = async () => {
        const inspectionResponse = await loadLatestOrderInspection({
          orderId: orderIdParam,
          request_id: requestIdParam || undefined,
        });

        if (inspectionResponse.data?.result) {
          loadFromEndpoint(inspectionResponse.data.result);
          toast.success("Latest order inspection data loaded successfully");
        } else {
          toast.error("Latest inspection data for this order is not available");
        }
      };

      loadData();
      loadSingleOrderData(orderIdParam);
    }
  }, [
    orderIdParam,
    requestIdParam,
    loadLatestOrderInspection,
    loadSingleOrderData,
    loadFromEndpoint,
  ]);

  return (
    <>
      {!isChrome && <ChromeUsageReminder />}

      <div className="max-w-screen w-screen h-screen">
        <PanelGroup direction="vertical">
          <Header
            title={NavigationItems.GCG.name}
            exclude={[NavigationItems.GCG.id]}
          />
          <PanelGroup direction="horizontal">
            <Box
              className="border-r"
              width="340px"
              minWidth="340px"
              height="100%"
            >
              <LeftSidebar
                isLoadingOrderInspection={isLoadingLatestOrderInspection}
              />
            </Box>

            <PanelGroup direction="vertical">
              <Flex justify="between" align="center">
                <Flex align="center" gap="2">
                  <Checkbox
                    className="ml-2"
                    label="Visualize Track Projections"
                    checked={visualizeTrackProjections}
                    onCheckedChange={(checked) =>
                      dispatch(setVisualizeTrackProjections(Boolean(checked)))
                    }
                  />
                  <Checkbox
                    className="ml-2"
                    label="Toggle SKU Planogram"
                    checked={isPlanogramViewVisible}
                    onCheckedChange={(checked) =>
                      setIsPlanogramViewVisible(Boolean(checked))
                    }
                  />
                </Flex>

                <AnnotationHeader
                  hasAnnotationViewSelector={false}
                  hasGondolaSelectDisabled={true}
                />
              </Flex>
              <Panel id="workspace">
                <Flex
                  justify="center"
                  className="relative h-full max-w-full max-h-full flex justify-center items-center bg-black text-white"
                >
                  <AnnotationWorkspace
                    key={workspaceKey}
                    isClickable={false}
                    hasZoneProjections={false}
                    selectedOrder={selectedOrder}
                    renderToCanvas={() => (
                      <>
                        {isPlanogramViewVisible && (
                          <SkuSectionChangesPlanogram />
                        )}
                        <WristPoint />
                        <HandProjections />
                        <TrackPins />
                      </>
                    )}
                    renderOverlay={() => (
                      <>
                        <Flex className="absolute top-4 left-4 z-30 gap-2">
                          <TrackPinsStatus />
                          <FrameAvailabilityStatus />
                        </Flex>
                        <LegendBox />
                      </>
                    )}
                  />
                </Flex>
              </Panel>
              <ZoomPanInstructions />
              <PanelResizeHandle className="h-2 bg-gray-200 active:bg-gray-600 hover:bg-gray-400 hover:transition-1" />
              <Panel
                id="playback-controls"
                defaultSize={convertHeightToPercent(250)}
                collapsible
              >
                <div className="w-full bg-white h-full border-top shadow-sidebar">
                  <TimelineControls
                    onUpdateZoom={handleUpdateZoom}
                    onRangeChange={handleRangeChange}
                    navigationMode={NAVIGATION_MODES.GONDOLA_CHANGES}
                  />
                  <Timeline ref={timelineRef} />
                </div>
              </Panel>
            </PanelGroup>

            <Box
              className="border-l"
              width="500px"
              minWidth="500px"
              height="100%"
            >
              <RightSidebar />
            </Box>
          </PanelGroup>
        </PanelGroup>
      </div>
    </>
  );
}
